import { useState } from 'react';
import { toast } from 'sonner';
import { UserPlus } from 'lucide-react';

import { useCreateAdminUserMutation } from '@/client/api/admin/users.mutation';
import type { AdminUserCreatePayload } from '@/client/types/admin/users-orgs';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface AddUserDialogProps {
  open: boolean;
  onClose: () => void;
}

const EMPTY: AdminUserCreatePayload = {
  first_name: '',
  last_name: '',
  email1: '',
  phone1: '',
  locale: '',
  password: '',
};

/**
 * Create a user.
 *
 * `POST /api/admin/users` discards the new `uid` (gaps §6) — it returns only
 * `result['user_account']`. There is therefore no id to deep-link to, so the
 * dialog just closes and lets the invalidated list refetch. Once the backend
 * returns `uid` this can open the new user's drawer directly.
 */
const AddUserDialog = ({ open, onClose }: AddUserDialogProps) => {
  const [form, setForm] = useState<AdminUserCreatePayload>(EMPTY);
  const createMutation = useCreateAdminUserMutation();

  const setField = (key: keyof AdminUserCreatePayload, value: string) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const handleClose = () => {
    setForm(EMPTY);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim()) return;

    const toastId = toast.loading('Creating user…');
    try {
      // The hook form-encodes (gap C-1); pass the plain payload.
      await createMutation.mutateAsync(form);
      toast.success('User created', {
        id: toastId,
        description: `${form.first_name} ${form.last_name} has been added. The list has been refreshed.`,
        richColors: true,
        duration: 4000,
      });
      handleClose();
    } catch (error: any) {
      toast.error('Failed to create user', {
        id: toastId,
        description: error?.message ?? 'Something went wrong.',
        richColors: true,
        duration: 5000,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) handleClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-blue-600" />
            Add User
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="user-first-name">
                First name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="user-first-name"
                value={form.first_name}
                onChange={e => setField('first_name', e.target.value)}
                required
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="user-last-name">
                Last name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="user-last-name"
                value={form.last_name}
                onChange={e => setField('last_name', e.target.value)}
                required
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="user-email">Email</Label>
            <Input
              id="user-email"
              type="email"
              placeholder="name@example.com"
              value={form.email1 ?? ''}
              onChange={e => setField('email1', e.target.value)}
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="user-phone">Phone</Label>
            <Input
              id="user-phone"
              placeholder="+233…"
              value={form.phone1 ?? ''}
              onChange={e => setField('phone1', e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="user-locale">Locale</Label>
              <Input
                id="user-locale"
                placeholder="en_GB"
                value={form.locale ?? ''}
                onChange={e => setField('locale', e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="user-password">Password</Label>
              <Input
                id="user-password"
                type="password"
                placeholder="Optional"
                value={form.password ?? ''}
                onChange={e => setField('password', e.target.value)}
              />
            </div>
          </div>

          <p className="text-xs text-gray-500">
            The create endpoint does not return the new user&apos;s id, so the list is
            refreshed after saving rather than opening the new record.
          </p>

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                createMutation.isPending ||
                !form.first_name.trim() ||
                !form.last_name.trim()
              }
            >
              {createMutation.isPending ? 'Creating…' : 'Create User'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddUserDialog;
